/**
 * Sizes on the storefront product page (Events & Decor plan, P3 phase 0).
 *
 * Two automatic rules until a per-product "show sizes on the website" switch
 * exists:
 *  - an item with exactly one size shows no size picker (the one size is still
 *    auto-picked, so add-to-cart is unchanged);
 *  - the Size Guide — a clothing table (XS–XL, bust/waist/hips) — shows only
 *    for items sized in a clothing or shoe size system, never for flowers,
 *    event decor, perfume volumes, bags, rings or kids' ages.
 *
 * The page reads no size-system row (no new query), only what the product
 * carries: `fashionAttributes.sizeSystem` (a key — the preset id
 * "shoes_us_women" or the DB slug "shoes-us-women"), `fashionAttributes.sizes`,
 * `product.industry` and its catalogue shelf (`category` / `subcategory` /
 * `productType` — the storefront's product query already returns them). Kept
 * pure so it can be tested.
 */
import { SIZE_SYSTEMS, type SizeSystem } from "@/lib/config/size-systems";

type SizeSystemCategory = SizeSystem["category"];

/** "shoes_us_women" / "shoes-us-women" and "oneSize" / "one-size" name the same system. */
function systemKey(key: unknown): string {
  return String(key ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** "2XL", " 2xl " and "2 XL" are the same size. */
function sizeKey(size: unknown): string {
  return String(size ?? "").toLowerCase().replace(/\s+/g, "");
}

const isApparel = (category: SizeSystemCategory | null) =>
  category === "clothing" || category === "shoe";

const CATEGORY_BY_KEY = new Map<string, SizeSystemCategory>(
  Object.values(SIZE_SYSTEMS).map((s) => [systemKey(s.id), s.category])
);

/**
 * Every size a clothing or shoe system offers (the DB rows carry the same
 * labels as these presets), plus the older spellings of the big letter sizes.
 */
const APPAREL_SIZES = new Set<string>(
  [
    ...Object.values(SIZE_SYSTEMS)
      .filter((s) => isApparel(s.category))
      .flatMap((s) => s.sizes),
    "XXL",
    "XXXL",
    "XXXXL",
  ].map(sizeKey)
);

/**
 * The kind of a size system from its key, or null when the key is missing or
 * unknown. A system added in Platform Admin after the presets keeps its
 * category on its own row, which this page doesn't read — so only a name that
 * says shoes is recognised; anything else is not assumed to be clothing.
 */
export function sizeSystemCategory(key: string | null | undefined): SizeSystemCategory | null {
  const k = systemKey(key);
  if (!k) return null;
  const known = CATEGORY_BY_KEY.get(k);
  if (known) return known;
  if (k.includes("shoe") || k.includes("footwear")) return "shoe";
  return null;
}

/** A category, subcategory or product type, as the product query returns it. */
export interface CatalogueShelf {
  name?: string | null;
  slug?: string | null;
}

export interface SizedProduct {
  industry?: string | null;
  category?: CatalogueShelf | null;
  subcategory?: CatalogueShelf | null;
  productType?: CatalogueShelf | null;
  fashionAttributes?: {
    sizeSystem?: string | null;
    sizes?: readonly string[] | null;
  } | null;
}

/** "Event Décor" and "event_decor" both read "event decor" — whole words, no accents. */
function shelfWords(shelf: CatalogueShelf): string {
  return `${shelf.name ?? ""} ${shelf.slug ?? ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ");
}

const EVENTS_WORD = /\b(events?|decor\w*|flowers?|bouquets?|balloons?)\b/;
const GARMENT_WORD =
  /\b(wear|clothing|apparel|dress(es)?|gowns?|tops?|blouses?|shirts?|skirts?|sleeves?|trousers|pants|suits?|jackets?|shoes?)\b/;

/**
 * Does the product sit on an events shelf — event decor, flowers, bouquets,
 * balloons? A shelf that also names a garment ("Event Wear", "Flower Girl
 * Dresses", "Balloon Sleeve Tops") is still clothing.
 */
export function onEventsShelf(product: SizedProduct): boolean {
  return [product.category, product.subcategory, product.productType].some((shelf) => {
    if (!shelf) return false;
    const words = shelfWords(shelf);
    return EVENTS_WORD.test(words) && !GARMENT_WORD.test(words);
  });
}

/**
 * Is this something a person wears — the only kind the Virtual Try-On can
 * dress a photo in? Not a registry industry drawn on the fashion template
 * (Events & Decor), and not decor saved as fashion (a bouquet under Event
 * Decor > Flowers once offered "Virtual Try-On").
 */
export function isWearableProduct(product: SizedProduct | null | undefined): boolean {
  if (!product) return false;
  const industry = String(product.industry ?? "").trim().toLowerCase();
  if (industry && industry !== "fashion") return false;
  return !onEventsShelf(product);
}

/** Does this product get the clothing Size Guide? */
export function showsClothingSizeGuide(product: SizedProduct | null | undefined): boolean {
  if (!product) return false;

  // A registry industry drawn on the fashion template (Events & Decor, say)
  // is not clothing, whatever its size system says.
  const industry = String(product.industry ?? "").trim().toLowerCase();
  if (industry && industry !== "fashion") return false;

  // Decor saved as a fashion product passes every size check below — Petal's
  // bouquets under Event Decor > Flowers are "International", size L. Its
  // shelf gives it away.
  if (onEventsShelf(product)) return false;

  // A system the owner chose on purpose is trusted as it is: EU half sizes
  // ("37.5"), odd waists ("27") and "UK 8" aren't in the preset lists.
  const system = product.fashionAttributes?.sizeSystem;
  const key = systemKey(system);
  if (key && key !== "international") return isApparel(sizeSystemCategory(system));

  // The studio opens on "International", so decor saved without changing it
  // still says "international" (and products saved before the key existed say
  // nothing) — their sizes ("Small", "3ft", "50ml") give them away. At least
  // one real clothing or shoe size is required.
  const sizes = product.fashionAttributes?.sizes ?? [];
  return sizes.some((s) => APPAREL_SIZES.has(sizeKey(s)));
}
